import { useId } from 'react';

/**
 * Charts drawn as inline SVG.
 *
 * No charting library: the three shapes needed here are a stacked bar, a
 * sparkline and a donut, and the smallest credible library is ~50KB gzipped —
 * a third of the entire budget for something a hundred lines of SVG covers.
 * These also inherit the theme tokens, which a library would fight.
 */

export interface SeriesPoint {
  day: string;
  inbound: number;
  outbound: number;
  internal: number;
  answered: number;
  total: number;
  duration_sec: number;
  spend: string;
}

const fmtDay = (iso: string): string =>
  new Date(iso + 'T00:00:00Z').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });

/**
 * Stacked daily call volume.
 *
 * Stacked rather than grouped: the question is "how busy were we", with the
 * mix as secondary. Grouped bars answer neither well at 30 points.
 */
export function VolumeChart({ data, height = 150 }: { data: SeriesPoint[]; height?: number }) {
  const id = useId();
  const max = Math.max(1, ...data.map((d) => d.inbound + d.outbound + d.internal));
  const n = Math.max(1, data.length);
  const gap = 2;
  const bw = Math.max(2, (100 - gap * (n - 1)) / n);

  const bars = data.map((d, i) => {
    const x = i * (bw + gap);
    const total = d.inbound + d.outbound + d.internal;
    let y = 100;
    const seg = (v: number, fill: string, key: string) => {
      if (v === 0) return null;
      const h = (v / max) * 100;
      y -= h;
      return <rect key={key} x={x} y={y} width={bw} height={h} fill={fill} rx={0.6} />;
    };
    return (
      <g key={d.day}>
        <title>{`${fmtDay(d.day)} — ${total} calls (${d.inbound} in, ${d.outbound} out, ${d.internal} internal)`}</title>
        {seg(d.internal, 'var(--arc-fg-4)', 'i')}
        {seg(d.outbound, 'var(--arc-accent)', 'o')}
        {seg(d.inbound, 'var(--arc-ok)', 'n')}
        {total === 0 && <rect x={x} y={99.4} width={bw} height={0.6} fill="var(--arc-border)" />}
      </g>
    );
  });

  return (
    <div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none"
        style={{ width: '100%', height, display: 'block' }}
        role="img" aria-labelledby={`${id}-t`}>
        <title id={`${id}-t`}>Daily call volume over {data.length} days</title>
        {bars}
      </svg>
      <div className="arc-row" style={{ justifyContent: 'space-between', marginTop: 6 }}>
        <span className="arc-subtle" style={{ fontSize: 11 }}>
          {data[0] ? fmtDay(data[0].day) : ''}
        </span>
        <div className="arc-row" style={{ gap: 12 }}>
          <Legend colour="var(--arc-ok)" label="inbound" />
          <Legend colour="var(--arc-accent)" label="outbound" />
          <Legend colour="var(--arc-fg-4)" label="internal" />
        </div>
        <span className="arc-subtle" style={{ fontSize: 11 }}>
          {data.length ? fmtDay(data[data.length - 1]!.day) : ''}
        </span>
      </div>
    </div>
  );
}

function Legend({ colour, label }: { colour: string; label: string }) {
  return (
    <span className="arc-row" style={{ gap: 4, fontSize: 11 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: colour }} aria-hidden="true" />
      <span className="arc-subtle">{label}</span>
    </span>
  );
}

/** A single-series line, for spend or duration trends. */
export function Sparkline({ values, height = 44, colour = 'var(--arc-accent)' }: {
  values: number[]; height?: number; colour?: string;
}) {
  const id = useId();
  if (values.length < 2) {
    return <div style={{ height }} className="arc-subtle">not enough data yet</div>;
  }
  const max = Math.max(1, ...values);
  const step = 100 / (values.length - 1);
  const pts = values.map((v, i) => `${i * step},${100 - (v / max) * 92}`);

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none"
      style={{ width: '100%', height, display: 'block' }}
      role="img" aria-labelledby={`${id}-t`}>
      <title id={`${id}-t`}>Trend over {values.length} days, peak {max}</title>
      {/* Fill first so the stroke sits on top of its own shading. */}
      <polygon points={`0,100 ${pts.join(' ')} 100,100`} fill={colour} opacity={0.12} />
      <polyline points={pts.join(' ')} fill="none" stroke={colour}
        strokeWidth={1.6} vectorEffect="non-scaling-stroke"
        strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/** Answer-rate donut. Reads at a glance; the number is inside it. */
export function Donut({ value, label, size = 92 }: {
  value: number; label: string; size?: number;
}) {
  const pct = Math.max(0, Math.min(100, value));
  const r = 15.9155;
  const tone = pct >= 70 ? 'var(--arc-ok)' : pct >= 40 ? 'var(--arc-warn)' : 'var(--arc-danger)';
  return (
    <div style={{ display: 'grid', placeItems: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 36 36" role="img" aria-label={`${label}: ${pct}%`}>
        <circle cx="18" cy="18" r={r} fill="none" stroke="var(--arc-surface-3)" strokeWidth="3.2" />
        <circle
          cx="18" cy="18" r={r} fill="none" stroke={tone} strokeWidth="3.2"
          strokeDasharray={`${pct} ${100 - pct}`} strokeDashoffset="25"
          strokeLinecap="round" transform="rotate(-90 18 18)"
        />
        <text x="18" y="18.2" textAnchor="middle" dominantBaseline="middle"
          style={{ font: '600 7px var(--arc-font)', fill: 'var(--arc-fg)' }}>
          {pct}%
        </text>
      </svg>
      <span className="arc-subtle" style={{ fontSize: 11, marginTop: 4 }}>{label}</span>
    </div>
  );
}
